import { Spin } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { get } from '../../../api/client';
import { NamespaceConfigForm, type NamespaceConfig } from '../../settings/components/NamespaceConfigForm';

function useNamespaceConfig(name: string) {
  return useQuery({
    queryKey: ['system-config', name],
    queryFn: async () => (await get<unknown>(`/admin/system-config/${name}`)) as NamespaceConfig,
  });
}

/**
 * 注册赠送规则（system_config 的 membership 命名空间）。
 *
 * 字段 schema 由后端下发（单位与叠加规则的语义都写在那里的 tip 里），本面板只渲染与保存：
 * 发放动作在注册流程里，关掉开关即刻停发，已送的时长不受影响。
 * 它原先在「系统配置 → 会员赠送」，与活动、批量赠送、发放记录分在两处，
 * 现在与会员域的其他读写口同页（会员是同一个业务域，配置不该散落两处）。
 */
export function SignupGiftRulePanel() {
  const { data, isLoading } = useNamespaceConfig('membership');
  if (isLoading || !data) return <Spin />;
  return (
    <NamespaceConfigForm
      config={data}
      cardTitle="注册赠送规则"
      switchCardKeys={['signupGiftEnabled']}
      labelOverrides={{
        signupGiftEnabled: {
          label: '注册成功后赠送会员',
          desc: '每个新账号注册成功即按下方的单位与时长发放付费档会员；关闭即停发，已发放的时长不受影响。注册链接命中活动时发的是活动那份，本条不参与（二选一）',
        },
      }}
    />
  );
}

/**
 * 邀请减免规则（system_config 的 referral 命名空间）。
 *
 * 与「推广链接」是两套独立规则：邀请要求好友真实付费才计酬，推广链接按短码优惠与奖励配置。
 * 反作弊的两道（最低实付额、月度计酬上限）也在这里，改完即刻对下一笔计酬生效。
 */
export function ReferralRulePanel() {
  const { data, isLoading } = useNamespaceConfig('referral');
  if (isLoading || !data) return <Spin />;
  return (
    <NamespaceConfigForm
      config={data}
      cardTitle="邀请减免规则"
      switchCardKeys={['enabled']}
      labelOverrides={{
        enabled: {
          label: '启用邀请减免',
          desc: '开启后用户可生成专属邀请码分享给好友；好友通过邀请码注册并真实付费后，双方按下方规则获得减免与奖励。关闭后邀请码不再生效、不再计酬',
        },
      }}
    />
  );
}
